import useSWR from "swr";
import * as http from "@/utils/request";
import { OSSMappings, OSSModel, RFResponseCommon } from "@/interfaces/common";
import { useEffect, useState } from "react";
import OSS from "ali-oss";
import getUUID from "@/utils/getUUID";
import parseUrl from "url-parse";
import rfStore from "@/utils/rfStore";
import log2Sentry from "@/utils/log2Sentry";
import { UploadError } from "@/utils/sentryError";

const BASE_URL = process.env.NEXT_PUBLIC_BASE_API;

/**
 * OSS 上传或者下载
 * @param { OSSMappings } mappingKey
 * @return 上传方法 预览方法
 */
const useOSS = (mappingKey: keyof OSSMappings) => {
	const uid = rfStore.getRFState()?.userId;
	const url = `${BASE_URL}/storage/api/storage/token/oss`;
	const [client, setClient] = useState<OSS>();
	const [loading, setLoading] = useState(false);

	const {
		data: OSSData,
		error,
		mutate,
	} = useSWR(
		uid ? url : null,
		async (url) => await http.post<null, RFResponseCommon<OSSModel>>(url).then((res) => res.data),
	);

	useEffect(() => {
		if (!OSSData) return;

		const posterConfig = OSSData.mappings[mappingKey];
		setClient(
			new OSS({
				region: posterConfig.region,
				endpoint: posterConfig.endpoint,
				accessKeyId: OSSData.accessKeyId,
				accessKeySecret: OSSData.accessKeySecret,
				stsToken: OSSData.securityToken,
				bucket: posterConfig.bucket,
			}),
		);
	}, [OSSData, mappingKey]);

	/**
	 * 上传文件到OSS
	 * @param file
	 * @param _fileName 自定义文件名 eg：aaa.jpg
	 * @returns OSS 相对地址
	 */
	const upload = async (file: File | Blob | Buffer, _fileName?: string, isFullUrl?: boolean) => {
		try {
			if (!client || !OSSData) throw Error("missing OSS config");

			const fileName = _fileName ? _fileName : `${getUUID()}.jpg`;
			const path = OSSData.mappings[mappingKey].folder + "/" + fileName;

			setLoading(true);
			await client.put(path, file);
			if (isFullUrl) {
				return `${process.env.NEXT_PUBLIC_APP_OSS_UPLOAD_CND}/${path}`;
			}
			return path;
		} catch (error) {
			const msg = error instanceof Error ? error.message : "unknown error with upload";
			log2Sentry(new UploadError(msg), "error", {
				file: file,
			});
			throw error;
		} finally {
			setLoading(false);
		}
	};

	/**
	 * 相对地址获取oss临时访问地址
	 * @param {path} string oss 文件名 eg：/aaa/bbb-ccc.jpg
	 * @returns {String} oss 临时访问绝对地址
	 */
	const preview = (path?: string) => {
		try {
			if (!path) throw Error("missing params path");
			if (!client || !OSSData) throw Error("missing OSS config");

			const cdnName = OSSData.mappings[mappingKey]?.cdn.Global;
			const url = client.signatureUrl(path);
			const parsed = parseUrl(url);

			return parsed.set("hostname", cdnName).toString();
		} catch (error) {
			return "";
		}
	};

	return {
		error,
		upload,
		preview,
		loading,
		mutate,
		// TODO: process、abort
	};
};

export default useOSS;
